"use client";

import React, {
  startTransition,
  useActionState,
  useEffect,
  useState,
} from "react";
import ConfirmModal from "../ui/ConfirmModal";
import { OPEN_HOUSE_INITIAL_STATE } from "@/services/form.service";
import { CaseData, CMSData } from "@/cms/models";
import { HomepageOpenhouseFraction } from "@/utils/gql/graphql";
import { submitOpenhouseForm } from "@/actions/openhouse-form";
import { formatDate, formatTime } from "@/utils/helper";
import TextInput from "../ui/inputs/TextInput";
import TextAreaInput from "../ui/inputs/TextAreaInput";
import OpenHouseSelect from "../ui/inputs/OpenHouseSelect";
import FormLayout from "../ui/inputs/FormLayout";
import { useReCaptcha } from "next-recaptcha-v3";

const OpenHouseForm: React.FC<{
  caseData: CaseData;
  cmsData: CMSData;
}> = ({ cmsData, caseData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalidCaptchaOpen, setIsInvalidCaptchaOpen] = useState(false);
  const [selected, setSelected] = useState<HomepageOpenhouseFraction | null>(
    caseData.openhouses?.filter((x) => x.bookingRequired)[0]
  );
  const [formState, formAction, isPending] = useActionState(
    (prevState: any, formData: FormData) => {
      formData.append("caseId", caseData!.id!);
      formData.append("appointmentId", selected?.appointmentId ?? "");
      return submitOpenhouseForm(prevState, formData);
    },
    OPEN_HOUSE_INITIAL_STATE
  );
  useEffect(() => {
    if (!("recaptchaIsValid" in formState)) {
      return;
    }

    console.log("Recaptcha:", {
      enabled: formState.recaptchaIsEnabled,
      validToken: formState.recaptchaIsValid,
    });

    if (!formState.recaptchaIsValid) {
      setIsInvalidCaptchaOpen(true);
    } else {
      setIsOpen(formState.isSuccess ?? false);
    }
  }, [formState]);
  const { firstName, lastName, address, zip, city, phone, email, message } =
    formState?.data || {};

  const { executeRecaptcha } = useReCaptcha();
  const submit = (payload: FormData) => {
    (async () => {
      try {
        const token = await executeRecaptcha("AssessmentForm");
        payload.append("recaptchaToken", token);
      } catch (error) {
        console.log("[ReCaptcha Error]:", error);
        payload.delete("recaptchaToken");
      } finally {
        startTransition(() => {
          formAction(payload);
        });
      }
    })();
  };

  return (
    <div className="homepage-text-sm">
      <ConfirmModal
        description="Får du brug for at ændre, justere eller aflyse din bestilling, kan du kontakte os på:"
        title="Tilmeld dig åbent hus"
        setOpen={(isOpen) => setIsOpen(isOpen)}
        isOpen={isOpen}
        contactInfo={{
          phone: caseData?.resourceGroup?.formattedPhone ?? "",
          email: caseData?.resourceGroup?.email ?? "",
        }}
      >
        <div className="flex flex-col">
          <div className="homepage-text-sm mb-3">
            <div>{caseData!.advertisementAddressOrDisplayName}</div>
            <div>{`${formatDate(selected?.startDate, { weekday: "long", day: "numeric", month: "long" })} ${formatTime(selected?.startDate)} - ${formatTime(selected?.endDate)}`}</div>
          </div>
          <div className="text-[var(--tabs-active-text-color)]">
            Åbent hus afholdes kun såfremt, der er tilmeldinger. Bliver du
            forhindret i at deltage, er det derfor vigtigt, at du kontakter os.
          </div>
        </div>
      </ConfirmModal>
      <ConfirmModal
        modalType="error"
        description="Prøv venligst igen. Hvis du fortsætter med at opleve denne fejl så kontakt venligst support."
        title="Din forespørgsel blev blokeret"
        setOpen={(isOpen) => setIsInvalidCaptchaOpen(isOpen)}
        isOpen={isInvalidCaptchaOpen}
      />
      <div className="flex flex-col gap-4 flex-1 w-full">
        <div className="flex flex-col">
          <div className="text-[var(--tabs-active-text-color)]">
            Åbent hus afholdes kun såfremt, der er tilmeldinger. Bliver du
            forhindret i at deltage, er det derfor vigtigt, at du kontakter os.
          </div>
        </div>
        <FormLayout
          submitLabel="Tilmeld åbent hus"
          featureFlags={cmsData?.featureFlags}
          isPending={isPending}
          formAction={submit}
        >
          <OpenHouseSelect
            onChange={setSelected}
            label="Muligheder"
            selected={selected}
            cmsData={cmsData}
            errors={formState?.zodErrors?.appointmentId}
            caseData={caseData}
          />
          <TextInput
            cmsData={cmsData}
            name="firstName"
            label="Fornavn"
            defaultValue={firstName}
            errors={formState?.zodErrors?.firstName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            name="lastName"
            label="Efternavn"
            defaultValue={lastName}
            errors={formState?.zodErrors?.lastName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-6"
            name="address"
            label="Adresse"
            defaultValue={address}
            errors={formState?.zodErrors?.address}
            required={cmsData?.forms.addressRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-2"
            name="zip"
            label="Postnr"
            defaultValue={zip}
            errors={formState?.zodErrors?.zip}
            required={cmsData?.forms.zipRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-4"
            name="city"
            label="By"
            defaultValue={city}
            errors={formState?.zodErrors?.city}
            required={cmsData?.forms.cityRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="phone"
            label="Telefonnummer"
            defaultValue={phone}
            errors={formState?.zodErrors?.phone}
            required={cmsData?.forms.phoneRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="email"
            label="E-mail"
            defaultValue={email}
            errors={formState?.zodErrors?.email}
            required={cmsData?.forms.emailRequired}
          />
          <TextAreaInput
            cmsData={cmsData}
            name="message"
            label="Besked"
            defaultValue={message}
            errors={formState?.zodErrors?.message}
            required={false}
          />
        </FormLayout>
      </div>
    </div>
  );
};

export default OpenHouseForm;
